import { CARGO_TYPES } from '../config/cargoTypes.js';

// Maps a cargo type's `category` (src/config/cargoTypes.js) to the matching
// key on Port.terminals — the infrastructure gate a port needs before it can
// trade that category at all (src/systems/TradeSystem.js's buyCargo/sellCargo,
// src/systems/EconomySystem.js's stock seeding/trickle). A port specialized
// in oil has no container cranes to unload a container ship with — this is a
// deliberate hard block, not just a fee/efficiency penalty.
export const CARGO_CATEGORY_TERMINAL_KEYS = {
  bulk_liquid:  'bulkLiquid',
  bulk_dry:     'bulkDry',
  container:    'container',
  refrigerated: 'refrigerated',
  ro_ro:        'roro'
};

// Max ship cargo capacity (TEU) allowed to dock, by port size
const PORT_SIZE_LIMITS = {
  tiny:   500,
  small:  2000,
  medium: 8000,
  large:  15000,
  major:  99999
};

// Port-size factor for repair cost multiplier (bigger facilities = higher overhead)
const REPAIR_SIZE_FACTORS = {
  tiny:   0.85,
  small:  0.90,
  medium: 1.00,
  large:  1.10,
  major:  1.15
};

// Cargo stock multipliers by port size: [producedMin, producedMax, demandedMin, demandedMax]
export const PORT_STOCK_RANGES = {
  tiny:   [20,   80,   5,    20],
  small:  [50,  200,   15,   60],
  medium: [200, 800,   60,  250],
  large:  [500, 3000,  200, 1000],
  major:  [2000, 10000, 800, 4000]
};

export class Port {
  constructor(data) {
    this.id       = data.id;
    this.name     = data.name;
    this.country  = data.country;
    this.region   = data.region;
    this.position = { lat: data.lat, lng: data.lng };

    this.size         = data.size;               // tiny | small | medium | large | major
    this.maxShipSize  = data.maxShipSize || 'large_cargo';
    this.berthCount   = data.berthCount  || 4;

    // Ship capacity limit derived from port size
    this.maxShipCapacity = PORT_SIZE_LIMITS[this.size] || 99999;

    this.services = {
      fuel:      data.hasFuel      !== false,
      repair:    data.hasRepair   || false,
      shipyard:  data.hasShipyard || false,
      warehouse: data.hasWarehouse|| false
    };

    // Cargo-handling infrastructure — which categories this port can trade
    // at all (see canHandleCargo() below). Unlike `services`, every flag
    // here defaults to false: a port with no terminal data explicitly set
    // trades nothing, rather than silently trading everything.
    this.terminals = {
      bulkLiquid:   data.hasBulkLiquidTerminal   || false,
      bulkDry:      data.hasBulkDryTerminal      || false,
      container:    data.hasContainerTerminal    || false,
      refrigerated: data.hasRefrigeratedTerminal || false,
      roro:         data.hasRoRoTerminal         || false
    };

    this.wealthLevel      = data.wealthLevel      || 5;
    this.industrialLevel  = data.industrialLevel  || 5;
    this.populationLevel  = data.populationLevel  || 5;

    this.producedGoods = data.producedGoods || [];
    this.demandedGoods = data.demandedGoods || [];

    this.fuelPrice        = data.baseFuelPrice   || 500;
    this.dockingFee       = data.dockingFee      || 1000;
    this.loadingFeePerTEU = data.loadingFee      || 50;

    // Repair cost multiplier: wealth drives labor cost, size drives facility overhead
    // wealthLevel 1→0.76, 5→1.0, 10→1.3; then scaled by size factor
    const wealthFactor = 0.7 + ((this.wealthLevel || 5) / 10) * 0.6;
    const sizeFactor = REPAIR_SIZE_FACTORS[this.size] || 1.0;
    this.repairCostMultiplier = Math.round(wealthFactor * sizeFactor * 100) / 100;

    // Runtime state (not persisted — regenerated on load)
    this.dockedShips  = new Set();
    this.anchorQueue  = [];           // FIFO queue of ship IDs waiting for berths
  }

  /** Add a ship to the berth waiting queue (if not already queued) */
  enqueueShip(shipId) {
    if (!this.anchorQueue.includes(shipId)) {
      this.anchorQueue.push(shipId);
    }
  }

  /** Remove and return the first ship ID from the queue (or null) */
  dequeueShip() {
    return this.anchorQueue.length > 0 ? this.anchorQueue.shift() : null;
  }

  /** Remove a specific ship from the queue (e.g. on departure, sale, or force-dock) */
  removeFromQueue(shipId) {
    const idx = this.anchorQueue.indexOf(shipId);
    if (idx !== -1) this.anchorQueue.splice(idx, 1);
  }

  /** Check if a ship type can dock here based on cargo capacity */
  canAcceptShip(shipType) {
    if (!shipType) return true;
    return shipType.cargoCapacity <= this.maxShipCapacity;
  }

  /** Whether at least one berth is free */
  hasBerthAvailable() {
    return this.dockedShips.size < this.berthCount;
  }

  /** Number of free berths (0 if over capacity from legacy saves) */
  availableBerths() {
    return Math.max(0, this.berthCount - this.dockedShips.size);
  }

  /** Repair cost per condition point for a given ship type at this port */
  getRepairCostPerPoint(shipType) {
    const base = shipType?.maintenanceCostPerDay || 100;
    return Math.round(base * this.repairCostMultiplier);
  }

  /**
   * Whether this port has the infrastructure to trade a given cargo type at
   * all — independent of whether a ship can physically carry it
   * (canShipCarryCargo, src/config/cargoTypes.js) or whether the port
   * currently has stock. A cargo type whose category isn't in
   * CARGO_CATEGORY_TERMINAL_KEYS fails open (returns true) rather than
   * silently blocking a future category nobody's mapped yet.
   */
  canHandleCargo(cargoId) {
    const cargoDef = CARGO_TYPES[cargoId];
    if (!cargoDef) return false;
    const key = CARGO_CATEGORY_TERMINAL_KEYS[cargoDef.category];
    if (!key) return true;
    return this.terminals[key];
  }
}
